import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Share2, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { openCertificatePdf } from "@/lib/certificateDownload";

/**
 * Student-facing certificate page. Previously rendered a stylized HTML
 * mockup, which looked nothing like the actual PTIB-grade PDF (logos,
 * accreditation strip, signature). We now fetch the real ReportLab PDF
 * as an authenticated blob and embed it inline via <iframe> so the
 * preview matches the downloaded file exactly.
 */
export default function Certificate() {
    const { certId } = useParams();
    const [cert, setCert] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfError, setPdfError] = useState(null);
    const blobRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        // Metadata fetch (for student name / program title / verify link).
        api.get(`/certificates/${certId}`)
            .then((r) => { if (!cancelled) setCert(r.data); })
            .catch(() => navigate("/"))
            .finally(() => { if (!cancelled) setLoading(false); });
        // PDF blob fetch — uses the authenticated axios client so JWT
        // travels on the Authorization header (no token in the URL).
        api.get(`/certificates/${certId}/download`, { responseType: "blob" })
            .then(({ data }) => {
                if (cancelled) return;
                const url = URL.createObjectURL(
                    new Blob([data], { type: "application/pdf" }),
                );
                blobRef.current = url;
                setPdfBlobUrl(url);
            })
            .catch(() => {
                if (!cancelled) setPdfError("Could not load certificate PDF.");
            });
        return () => {
            cancelled = true;
            if (blobRef.current) {
                URL.revokeObjectURL(blobRef.current);
                blobRef.current = null;
            }
        };
    }, [certId, navigate]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">
                Loading certificate…
            </div>
        );
    }
    if (!cert) return null;

    const publicVerifyUrl = `${window.location.origin}/verify/${certId}`;
    const copyShareLink = () => {
        navigator.clipboard.writeText(publicVerifyUrl);
        toast.success("Public verify link copied");
    };

    return (
        <div className="max-w-5xl mx-auto px-6 py-10" data-testid="certificate-page">
            <Link to="/dashboard" className="text-sm text-muted-foreground hover:text-primary">
                ← Back to dashboard
            </Link>

            <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                        Certificate of Completion
                    </div>
                    <h1 className="font-display text-2xl text-primary mt-1 truncate">
                        {cert.program_title}
                    </h1>
                    <div className="text-sm text-muted-foreground mt-0.5">
                        Awarded to <strong>{cert.student_name}</strong> ·{" "}
                        Issued {new Date(cert.issued_at).toLocaleDateString()}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        variant="outline"
                        onClick={copyShareLink}
                        data-testid="share-verify-link-btn"
                    >
                        <Share2 className="h-4 w-4 mr-2" />
                        Copy verify link
                    </Button>
                    <Button
                        className="bg-primary hover:bg-primary/90"
                        data-testid="download-cert-btn"
                        onClick={() => openCertificatePdf(certId, { download: true })}
                    >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                    </Button>
                </div>
            </div>

            {/* Inline PDF preview — matches the downloaded file exactly. */}
            <div
                className="mt-6 rounded-md border border-border bg-card overflow-hidden shadow-sm"
                style={{ aspectRatio: "1.414 / 1" }}
                data-testid="certificate-pdf-frame-wrap"
            >
                {pdfError ? (
                    <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-destructive p-6">
                        <AlertCircle className="h-6 w-6" />
                        <span>{pdfError}</span>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openCertificatePdf(certId)}
                        >
                            Open in a new tab
                        </Button>
                    </div>
                ) : pdfBlobUrl ? (
                    <iframe
                        src={pdfBlobUrl}
                        title="Certificate PDF"
                        className="w-full h-full"
                        data-testid="certificate-pdf-frame"
                    />
                ) : (
                    <div className="h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        <span className="text-sm">Loading certificate…</span>
                    </div>
                )}
            </div>

            <p className="mt-4 text-xs text-muted-foreground">
                Share{" "}
                <span className="font-mono text-foreground/80">
                    /verify/{certId.slice(0, 8)}…
                </span>{" "}
                on LinkedIn or your résumé — anyone can confirm authenticity.
            </p>
        </div>
    );
}
